import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

interface FeeRecord {
  student_id: string;
  student_name: string;
  class: string;
  section: string | null;
  roll_no: number;
  fee_record_id: number | null;
  session: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: string;
  paid_amount: string;
  fees_status: string;
  pay_mode: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface OptionalFee {
  id: number;
  fee_name: string;
  amount: string;
}

interface MarkFeeModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string | null;
  session: string | null;
  monthYear: string | null;
  onSuccess: () => void;
  toast: React.RefObject<Toast | null>;
}

const MarkFeeModal: React.FC<MarkFeeModalProps> = ({
  open,
  onClose,
  studentId,
  session,
  monthYear,
  onSuccess,
  toast,
}) => {
  const [feeRecord, setFeeRecord] = useState<FeeRecord | null>(null);
  const [optionalFees, setOptionalFees] = useState<OptionalFee[]>([]);
  const [selectedOptionalFees, setSelectedOptionalFees] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open && studentId && session && monthYear) {
      fetchFeeRecord();
      fetchOptionalFees();
    } else if (!open) {
      resetState();
    }
  }, [open, studentId, session, monthYear]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.mark-fee-dialog');
      const mask = document.querySelector('.mark-fee-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const resetState = () => {
    setFeeRecord(null);
    setOptionalFees([]);
    setSelectedOptionalFees([]);
  };

  const fetchFeeRecord = async () => {
    setFetching(true);
    try {
      const response = await apiClient.get(
        `students/monthly-fees-records?student_id=${studentId}&session=${session}&start_month_year=${monthYear}&end_month_year=${monthYear}`
      );
      if (response.data.status === 'success' && response.data.data?.length > 0) {
        setFeeRecord(response.data.data[0]);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || 'Failed to fetch fee record',
          life: 5000,
        });
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load fee record',
        life: 5000,
      });
    } finally {
      setFetching(false);
    }
  };

  const fetchOptionalFees = async () => {
    try {
      const response = await apiClient.get(
        `fee-types/optional-active?student_id=${studentId}&session=${session}&month_year=${monthYear}`
      );
      if (response.data.status === 'success') {
        setOptionalFees(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch optional fees:', error);
    }
  };

  const handleOptionalFeeToggle = (feeId: number) => {
    setSelectedOptionalFees((prev) =>
      prev.includes(feeId) ? prev.filter((id) => id !== feeId) : [...prev, feeId]
    );
  };

  const calculateTotal = () => {
    const base = feeRecord ? parseFloat(feeRecord.total_amount) : 0;
    const optionalTotal = selectedOptionalFees.reduce((sum, feeId) => {
      const fee = optionalFees.find((f) => f.id === feeId);
      return sum + (fee ? parseFloat(fee.amount) : 0);
    }, 0);
    return (base + optionalTotal).toFixed(2);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const requestData: any = {
        student_id: studentId,
        session,
        month_year: monthYear,
      };

      if (selectedOptionalFees.length > 0) {
        requestData.optional_fees = selectedOptionalFees.map((feeId) => {
          const fee = optionalFees.find((f) => f.id === feeId);
          return {
            fee_id: parseInt(feeId.toString(), 10),
            amount: fee?.amount || '0.00',
          };
        });
      }

      const response = await apiClient.post('students/pay-fees', requestData);

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Fee marked as paid successfully!',
          life: 3000,
        });
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 300);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || 'Failed to mark fee as paid',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to mark fee as paid',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '54px',
        height: '54px',
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '22px',
        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
        flexShrink: 0,
        border: '3px solid white',
      }}>
        <i className="pi pi-indian-rupee"></i>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#1e40af',
          lineHeight: 1.2,
          marginBottom: '4px',
        }}>
          Mark Fee Payment
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          Record payment for {feeRecord?.student_name || 'student'} — {monthYear}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .mark-fee-dialog-mask {
          backdrop-filter: blur(4px);
          background-color: rgba(0, 0, 0, 0.25) !important;
          animation: mfMaskIn 0.35s ease;
        }
        .mark-fee-dialog-mask.mask-closing {
          animation: mfMaskOut 0.4s ease !important;
        }
        @keyframes mfMaskIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes mfMaskOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        .mark-fee-dialog {
          animation: mfDialogIn 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        .mark-fee-dialog.dialog-closing {
          animation: mfDialogOut 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        @keyframes mfDialogIn {
          from { opacity: 0; transform: scale(0.92) translateY(-20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes mfDialogOut {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to   { opacity: 0; transform: scale(0.88) translateY(25px); }
        }

        /* Optional fee checkbox rows */
        .optional-fee-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 10px;
          background: rgba(59, 130, 246, 0.04);
          border: 1px solid rgba(59, 130, 246, 0.08);
          cursor: pointer;
          transition: all 0.2s;
          user-select: none;
        }
        .optional-fee-row:hover {
          background: rgba(59, 130, 246, 0.09);
          border-color: rgba(59, 130, 246, 0.2);
        }
        .optional-fee-row.selected {
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.35);
        }
        .optional-fee-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 2px solid #94a3b8;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.2s;
          background: white;
        }
        .optional-fee-row.selected .optional-fee-checkbox {
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          border-color: transparent;
        }

        /* Skeleton shimmer */
        .shimmer {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 200% 100%;
          animation: shimmer 1.4s infinite;
          border-radius: 8px;
        }
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        className="mark-fee-dialog"
        maskClassName="mark-fee-dialog-mask"
        style={{
          width: '90vw',
          maxWidth: '560px',
          borderRadius: '24px',
          overflow: 'hidden',
        }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Student Information */}
          <div style={{
            background: 'rgba(255,255,255,0.8)',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid rgba(59,130,246,0.1)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
          }}>
            {/* Section header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '16px', paddingBottom: '12px',
              borderBottom: '1px solid rgba(229,231,235,0.6)',
            }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
              }}>
                <i className="pi pi-user" style={{ fontSize: '14px' }}></i>
              </div>
              <span style={{
                color: '#1e3a8a', fontWeight: 700, fontSize: '13px',
                letterSpacing: '0.05em', textTransform: 'uppercase',
              }}>Student Information</span>
            </div>

            {fetching ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div className="shimmer" style={{ height: '18px', width: '60%' }} />
                <div className="shimmer" style={{ height: '18px', width: '45%' }} />
                <div className="shimmer" style={{ height: '18px', width: '35%' }} />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { label: 'Name', value: feeRecord?.student_name },
                  {
                    label: 'Class',
                    value: feeRecord
                      ? `${feeRecord.class}${feeRecord.section ? ` — Section ${feeRecord.section.toUpperCase()}` : ''} (Roll No. ${feeRecord.roll_no})`
                      : null,
                  },
                  { label: 'Base Fee', value: feeRecord ? `₹${feeRecord.total_amount}` : null },
                ].map(({ label, value }) => (
                  <div key={label} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 12px', borderRadius: '8px',
                    background: 'rgba(248,250,252,0.8)',
                    border: '1px solid rgba(226,232,240,0.6)',
                  }}>
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, minWidth: '60px' }}>
                      {label}
                    </span>
                    <span style={{ fontSize: '13px', color: '#1e3a8a', fontWeight: 600 }}>
                      {value || 'N/A'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Optional Fees */}
          <div style={{
            background: 'rgba(255,255,255,0.8)',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid rgba(59,130,246,0.1)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '16px', paddingBottom: '12px',
              borderBottom: '1px solid rgba(229,231,235,0.6)',
            }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
              }}>
                <i className="pi pi-list" style={{ fontSize: '14px' }}></i>
              </div>
              <span style={{
                color: '#1e3a8a', fontWeight: 700, fontSize: '13px',
                letterSpacing: '0.05em', textTransform: 'uppercase',
              }}>Optional Fees</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {optionalFees.length === 0 ? (
                <div style={{
                  padding: '16px', textAlign: 'center',
                  color: '#94a3b8', fontSize: '13px', fontWeight: 500,
                  background: 'rgba(248,250,252,0.8)', borderRadius: '10px',
                  border: '1px dashed #e2e8f0',
                }}>
                  <i className="pi pi-info-circle" style={{ marginRight: '6px' }}></i>
                  No optional fees available
                </div>
              ) : (
                optionalFees.map((fee) => {
                  const isSelected = selectedOptionalFees.includes(fee.id);
                  return (
                    <div
                      key={fee.id}
                      className={`optional-fee-row${isSelected ? ' selected' : ''}`}
                      onClick={() => !loading && handleOptionalFeeToggle(fee.id)}
                    >
                      <div className="optional-fee-checkbox">
                        {isSelected && (
                          <i className="pi pi-check" style={{ fontSize: '10px', color: 'white' }}></i>
                        )}
                      </div>
                      <span style={{ fontSize: '13px', color: '#1e3a8a', fontWeight: 500, flex: 1 }}>
                        {fee.fee_name}
                      </span>
                      <span style={{
                        fontSize: '13px', fontWeight: 700, color: '#1e40af',
                        background: 'rgba(59,130,246,0.08)', padding: '2px 10px',
                        borderRadius: '20px', border: '1px solid rgba(59,130,246,0.15)',
                      }}>
                        ₹{fee.amount}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Total amount */}
            <div style={{
              marginTop: '16px',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              borderRadius: '12px',
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.85)' }}>
                <i className="pi pi-wallet" style={{ fontSize: '15px' }}></i>
                <span style={{ fontSize: '13px', fontWeight: 600 }}>Total Amount</span>
              </div>
              <span style={{ fontSize: '20px', fontWeight: 800, color: 'white', letterSpacing: '-0.5px' }}>
                ₹{calculateTotal()}
              </span>
            </div>
          </div>

          {/* Submit */}
          <div style={{ textAlign: 'center', marginTop: '4px' }}>
            <GlobalButton
              label={loading ? 'Processing...' : 'Mark as Paid'}
              onClick={handleSubmit}
              variant="primary"
              size="medium"
              disabled={loading || fetching}
              loading={loading}
              fullWidth={false}
            />
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default MarkFeeModal;